import {
  import_ebay_route,
  platform_products_route,
  VITE_API_URL,
} from "@/constants";
import type { AuthFetchParams } from "./global";
import type { ErrorResponse, PlatformProductsResponse } from "@/lib/types";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { getAccount } from "./ebay";
import { useAuth } from "@/providers/auth";
import { toast } from "sonner";
import i18n from "@/i18n";
import { useOrg } from "@/providers/organization";
import { sleep } from "@/lib/utils";
import type { BulkFilters, InventoryFilters, ProductSort } from "./product";
import { DEFAULT_PRODUCT_SORT, serverSearch } from "./product";

async function getProducts(
  pcId: string,
  params: AuthFetchParams,
  page: number,
  pageSize: number,
  filters: InventoryFilters,
  searchTerm = "",
  sort: ProductSort = DEFAULT_PRODUCT_SORT,
) {
  const search = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const trimmedSearchTerm = searchTerm.trim();
  if (trimmedSearchTerm) {
    search.set("search", trimmedSearchTerm);
  }
  search.set("sync_status", filters.sync);
  if (filters.pcIds.length > 0) {
    search.set("pc_ids", filters.pcIds.join(","));
  }
  if (filters.collectionIds.length > 0) {
    search.set("collection_ids", filters.collectionIds.join(","));
  }
  search.set("sort_by", sort.by);
  search.set("sort_dir", sort.dir);

  const res = await fetch(
    `${VITE_API_URL}${platform_products_route}/${pcId}?${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as PlatformProductsResponse;
}

export const ebayProductListQueryOpts = (
  alias: string,
  params: AuthFetchParams,
  page = 1,
  pageSize = 30,
  filters: InventoryFilters = { sync: "all", pcIds: [], collectionIds: [] },
  searchTerm = "",
  sort: ProductSort = DEFAULT_PRODUCT_SORT,
) => {
  const search = serverSearch(searchTerm);
  return queryOptions({
    queryKey: ["ebayProducts", alias, page, pageSize, filters, search, sort],
    queryFn: async () => {
      const ebayUser = await getAccount(alias, params);

      return await getProducts(
        ebayUser.platform_connection_id,
        params,
        page,
        pageSize,
        filters,
        search,
        sort,
      );
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const useImportEbayProductMutation = () => {
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();

  return useMutation({
    mutationFn: async ({
      alias,
      importIds,
      pcId,
      all,
      filters,
    }: {
      alias: string;
      importIds: string[];
      pcId: string;
      all: boolean;
      // all imports the products the page shows with these
      filters?: BulkFilters;
    }) => {
      const res = await fetch(`${VITE_API_URL}${import_ebay_route}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
        body: JSON.stringify({
          alias: alias,
          product_ids: importIds,
          pc_id: pcId,
          all,
          filters,
        }),
      });

      const access = res.headers.get("access-token");
      if (access) setToken(access);

      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }
    },
    onMutate: () => {
      toast.info(i18n.t("toast.importWillBegin"));
    },
    onSuccess: () => {
      toast.success(i18n.t("toast.importStarted"));
    },
    onError: async (error) => {
      if (error.message.includes("credits remaining")) {
        const err = JSON.parse(error.message) as ErrorResponse;
        toast.error(err.msg as unknown as string);
        await sleep(2000); // sleep for 1 second
        toast.info(i18n.t("toast.upgradeForCredits"));
      } else if (error.message.includes("Import all is limited")) {
        toast.error(i18n.t("toast.importAllLimit"));
      } else {
        toast.error(i18n.t("toast.importFailed"));
      }
    },
  });
};
